import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Users2, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { ProgressWave } from "@/components/ProgressWave";
import { api, money, formatApiError } from "@/lib/api";

const selectCls = "w-full rounded-xl border border-[#e4d8c4] bg-white px-3 py-2.5 text-sm text-[#00334a]";

export default function TeamsTab() {
  const [campaigns, setCampaigns] = useState([]);
  const [campaignId, setCampaignId] = useState("");
  const [teams, setTeams] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", description: "", goal: 0 });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/campaigns").then(({ data }) => {
      setCampaigns(data);
      if (data.length && !campaignId) setCampaignId(data[0].id);
    });
  }, []); // eslint-disable-line

  const loadTeams = useCallback(() => {
    if (!campaignId) return;
    api.get(`/teams?campaign_id=${campaignId}`).then(({ data }) => setTeams(data));
  }, [campaignId]);
  useEffect(() => { loadTeams(); }, [loadTeams]);

  const openNew = () => { setEditing(null); setForm({ name: "", description: "", goal: 0 }); setOpen(true); };
  const openEdit = (t) => { setEditing(t.id); setForm({ name: t.name, description: t.description || "", goal: t.goal || 0 }); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    if (!campaignId) { toast.error("Create a campaign first"); return; }
    setBusy(true);
    try {
      const payload = { campaign_id: campaignId, name: form.name, description: form.description, goal: Number(form.goal) || 0 };
      if (editing) await api.put(`/teams/${editing}`, payload);
      else await api.post("/teams", payload);
      toast.success(editing ? "Team updated" : "Team created");
      setOpen(false);
      loadTeams();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this team? Members will be unassigned.")) return;
    await api.delete(`/teams/${id}`);
    toast.success("Team deleted");
    loadTeams();
  };

  return (
    <div className="space-y-6" data-testid="admin-teams">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <span className="hf-eyebrow">Teams & supporters</span>
          <h1 className="mt-1 font-heading text-3xl font-extrabold text-[#00334a]">Teams</h1>
        </div>
        <div className="flex items-center gap-3">
          <select value={campaignId} onChange={(e) => setCampaignId(e.target.value)} className={selectCls} data-testid="teams-campaign-select">
            {campaigns.length === 0 && <option value="">No campaigns</option>}
            {campaigns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <Button onClick={openNew} className="rounded-full hf-btn-coral font-bold" data-testid="admin-create-team-button">
            <Plus className="mr-2 h-4 w-4" /> New team
          </Button>
        </div>
      </div>

      {teams.length === 0 ? (
        <div className="rounded-3xl border border-dashed border-[#e4d8c4] bg-white p-12 text-center">
          <Users2 className="mx-auto h-10 w-10 text-[#e4d8c4]" />
          <p className="mt-3 font-semibold text-[#00334a]">No teams in this campaign</p>
        </div>
      ) : (
        <div className="grid gap-5 lg:grid-cols-2">
          {teams.map((t) => {
            const pct = t.goal ? Math.min(100, Math.round((t.raised / t.goal) * 100)) : 0;
            return (
              <div key={t.id} className="rounded-3xl border border-[#e4d8c4] bg-white p-5" data-testid={`team-card-${t.id}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-heading text-xl font-bold text-[#00334a]">{t.name}</h3>
                    {t.description && <p className="mt-0.5 text-sm text-[#7c7a6a]">{t.description}</p>}
                  </div>
                  <div className="flex gap-1">
                    <Button size="icon" variant="ghost" onClick={() => openEdit(t)} className="h-8 w-8 text-[#3f6670]" data-testid={`team-edit-${t.id}`}><Pencil className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => remove(t.id)} className="h-8 w-8 text-[#c97049]" data-testid={`team-delete-${t.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </div>
                <div className="mt-3"><ProgressWave pct={pct} height="h-3" showMilestones /></div>
                <div className="mt-2 flex justify-between text-sm font-semibold">
                  <span className="text-[#65949c]">{money(t.raised)}</span>
                  <span className="text-[#7c7a6a]">of {money(t.goal)} · {pct}%</span>
                </div>
                <div className="mt-4 border-t border-[#efe6d6] pt-3">
                  <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-[#7c7a6a]">{t.member_count} members</div>
                  <div className="space-y-1.5">
                    {t.members.map((m) => (
                      <div key={m.id} className="flex items-center justify-between text-sm">
                        <span className="inline-flex items-center gap-2 text-[#00334a]"><User className="h-3.5 w-3.5 text-[#65949c]" />{m.name}</span>
                        <span className="font-semibold text-[#65949c]">{money(m.raised)}</span>
                      </div>
                    ))}
                    {t.members.length === 0 && <div className="text-sm text-[#a9a795]">No supporters yet — send an invite.</div>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid="team-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit team" : "New team"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="space-y-1.5"><Label>Team name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="team-name-input" /></div>
            <div className="space-y-1.5"><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} /></div>
            <div className="space-y-1.5"><Label>Goal ($)</Label><Input type="number" min="0" value={form.goal} onChange={(e) => setForm({ ...form, goal: e.target.value })} data-testid="team-goal-input" /></div>
            <DialogFooter><Button type="submit" disabled={busy} className="rounded-full hf-btn-coral font-bold" data-testid="team-save-button">{busy ? "Saving…" : "Save team"}</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
